"use client";

// Persistent floating chat entry on every page (Play 3 of the AI Astrologer
// prominence audit). Hidden on /free-kundali/ (and /hi/free-kundali/),
// /ai-astrologer/, /free-astrology-chat/ where InlineChatPreview /
// KundaliResult.ChatPanel already render the chat inline — a sticky pill on top
// of those would be redundant noise.

import Link from "next/link";
import { usePathname } from "next/navigation";

import { shouldShowStickyChatButton } from "@/lib/global-overlays";
import { safeSessionSet } from "@/lib/browser-storage";

export default function StickyChatButton() {
  const pathname = usePathname();
  if (!shouldShowStickyChatButton(pathname)) return null;
  const isHindi = pathname === "/hi" || pathname?.startsWith("/hi/");
  // Hindi must land on a chat-first surface, mirroring the English destination.
  // /hi/astrologers/ is a persona DIRECTORY whose cards are inert while
  // APP_AVAILABLE=false — routing the global chat CTA there was a dead end
  // (hi first_msg 6.7% vs en 29.8%, 30d to 2026-08-13).
  const chatHref = isHindi ? "/hi/muft-jyotish-chat/" : "/ai-astrologer/";
  // One label at every width, and it is the accessible name (WCAG 2.5.3): the
  // pill used to show "Ask Pandit-ji" while screen readers heard "Ask the AI
  // Jyotishi", and Hindi pages got romanized Hinglish (c-a11y-10).
  const label = isHindi ? "AI ज्योतिषी से पूछें" : "Ask AI Jyotishi";

  function handleClick() {
    try {
      safeSessionSet("seo_entry_path", pathname || "/");
    } catch {
      // Best-effort context handoff.
    }
    void import("@/lib/analytics-events").then(({ trackEvent }) => {
      trackEvent("sticky_chat_button_clicked", {
        source_path: pathname || "/",
      });
    });
  }

  return (
    <Link
      href={chatHref}
      prefetch={false}
      onClick={handleClick}
      lang={isHindi ? "hi" : "en"}
      // GlobalAgentEntry defers to any visible [data-agent-bridge], so the pill
      // and the contextual card never stack (C15). md, not sm: the bottom nav is
      // md:hidden, so at 640–767px a sm:bottom-6 pill sat under it. Below md it
      // also steps aside while a floating card (push prompt, agent card) is up,
      // instead of sitting untappable underneath it.
      data-agent-bridge="sticky"
      className="fixed bottom-[76px] right-4 z-40 flex min-h-11 items-center gap-2 rounded-full bg-surface-brand-strong px-4 py-3 text-body-small font-semibold text-text-on-brand shadow-card transition hover:scale-105 md:bottom-6 max-md:[body:has([data-floating-card])_&]:hidden"
    >
      <span aria-hidden className="text-body">
        ॐ
      </span>
      <span>{label}</span>
    </Link>
  );
}
